import { Component, AfterViewInit, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import math from 'mathjs';
import _ from 'lodash';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Constants } from '../../constants';

@Component({
  selector: 'gCodeDialog',
  templateUrl: 'gCode.html',
  styleUrls: ['gCode.scss']
})
export class GCodeDialog implements AfterViewInit {
  private pitch: number = 0;
  private holeDepth: number = 0;
  private pathRadius: number = 0;
  private ipm: number = 0;
  public isDisabled: boolean = true;
  public coordinates: FormGroup;
  constructor(
    public dialogRef: MatDialogRef<GCodeDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private constants: Constants
  ) {
    this.ipm = this.data['ipm'];
    this.pitch = this.data['pitch'];
    this.holeDepth = this.data['holeDepth'];
    this.pathRadius = this.data['pathRadius'] / 2;

    this.coordinates = this.formBuilder.group({
      x: new FormControl(0, [
        Validators.pattern(this.constants.DECIMAL_ONLY_REGEX),
        Validators.max(180),
        Validators.min(0)
      ]),
      y: new FormControl(0, [
        Validators.pattern(this.constants.DECIMAL_ONLY_REGEX),
        Validators.max(180),
        Validators.min(0)
      ]),
      z: new FormControl(0, [
        Validators.pattern(this.constants.DECIMAL_ONLY_REGEX),
        Validators.max(180),
        Validators.min(0)
      ]),
      codeBox: new FormControl({ value: '', disabled: true })
    });

    this.generateCode();
  }

  convertToGcode = (coordinate: number): string => {
    let decCoord = math.round(coordinate, 4);
    let intCoord = math.floor(coordinate);
    let gCodeCoord = _.toString(decCoord);

    if (decCoord === intCoord && decCoord != 0) {
      gCodeCoord = _.toString(decCoord) + '.';
    } else if (decCoord < 0) {
      if (decCoord > -1) {
        gCodeCoord = _.toString(decCoord).replace('-0.', '-.');
      }
    } else if (decCoord > 0) {
      if (decCoord < 1) {
        gCodeCoord = _.toString(decCoord).replace('0.', '.');
      }
    }

    return gCodeCoord;
  };

  resetGCode = () => {
    this.isDisabled = true;
    this.coordinates.get('x').patchValue(0, { emitEvent: false });
    this.coordinates.get('y').patchValue(0, { emitEvent: false });
    this.coordinates.get('z').patchValue(0, { emitEvent: true }); // only emit once to recalc
  };

  generateCode = () => {
    let helixTurns = this.holeDepth / this.pitch;
    let turnsInt = math.floor(helixTurns);
    let turnsRemain = helixTurns - turnsInt;
    let startAng = 2 * math.PI * (1 - turnsRemain);

    let xStart = this.pathRadius * math.cos(startAng);
    let yStart = this.pathRadius * math.sin(startAng);

    let iVector = 0 - xStart;
    let jVector = 0 - yStart;

    let xCenter = isNaN(parseFloat(this.coordinates.get('x').value)) ? 0 : parseFloat(this.coordinates.get('x').value);
    let yCenter = isNaN(parseFloat(this.coordinates.get('y').value)) ? 0 : parseFloat(this.coordinates.get('y').value);
    let zCenter = isNaN(parseFloat(this.coordinates.get('z').value)) ? 0 : parseFloat(this.coordinates.get('z').value);

    if (xCenter !== 0 || yCenter !== 0 || zCenter !== 0) {
      this.isDisabled = false;
    } else if (xCenter === 0 && yCenter === 0 && zCenter === 0) {
      this.isDisabled = true;
    }

    let gCode =
      'X' +
      this.convertToGcode(xStart + xCenter) +
      ' Y' +
      this.convertToGcode(yStart + yCenter) +
      ' Z' +
      this.convertToGcode(zCenter) +
      '\r\nG17 ';

    let zValue = zCenter - this.pitch;

    for (let i: number = 0; i < turnsInt; i++) {
      gCode =
        gCode +
        'G03 X' +
        this.convertToGcode(xStart + xCenter) +
        ' Y' +
        this.convertToGcode(yStart + yCenter) +
        ' Z' +
        this.convertToGcode(zValue);
      gCode = gCode + ' I' + this.convertToGcode(iVector) + ' J' + this.convertToGcode(jVector);

      if (i == 0) {
        gCode = gCode + ' F' + this.ipm + '\r\n';
      } else {
        gCode = gCode + '\r\n';
      }

      zValue = zValue - this.pitch;
    }

    if (turnsRemain > 0) {
      gCode =
        gCode +
        'G03 X' +
        this.convertToGcode(this.pathRadius + xCenter) +
        ' Y' +
        this.convertToGcode(yCenter) +
        ' Z' +
        this.convertToGcode(zCenter - this.holeDepth);
      gCode = gCode + ' I' + this.convertToGcode(iVector) + ' J' + this.convertToGcode(jVector) + '\r\n';
    }

    gCode =
      gCode +
      'G03 X' +
      this.convertToGcode(this.pathRadius + xCenter) +
      ' Y' +
      this.convertToGcode(yCenter) +
      ' Z' +
      this.convertToGcode(zCenter - this.holeDepth);
    gCode = gCode + ' I' + this.convertToGcode(0 - this.pathRadius) + ' J0';

    this.coordinates.get('codeBox').patchValue(gCode, { emitEvent: false });
  };

  ngAfterViewInit() {
    this.coordinates
      .get('x')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.generateCode();
      });

    this.coordinates
      .get('y')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.generateCode();
      });

    this.coordinates
      .get('z')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.generateCode();
      });
  }
}
